import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, IconButton, Modal, TextArea, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { RequirePermission } from "@/permissions/RequirePermission";
import { EPermission } from "@/permissions/types";
import { useUpdateBookingMutation } from "@/redux/apis/booking/bookingApi";
import { addNotification, addToast } from "@/utils";
import { useSelectedBooking } from "../hooks/useSelectedBooking";

interface DashboardNotesEditProps {
  open: boolean;
  onClose: () => void;
}

const schema = z.object({
  dashboardNotes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export const DashboardNotesEdit = ({ open, onClose }: DashboardNotesEditProps) => {
  const {
    id: bookingId,
    notes: { dashboard },
  } = useSelectedBooking();
  const [updateBooking, { isLoading: isSaving }] = useUpdateBookingMutation();

  const form = useForm<{ dashboardNotes?: string }>({
    defaultValues: {
      dashboardNotes: dashboard,
    },
  });

  const {
    formState: { isDirty },
    register,
    handleSubmit,
  } = form;

  const onSubmit = async (data: FormData) =>
    updateBooking({
      bookingId,
      dashboard_note: data.dashboardNotes,
    })
      .unwrap()
      .then(() => {
        addNotification("success", "Notes Updated", "Dashboard notes updated successfully");
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));

  return (
    <RequirePermission permission={EPermission.DashboardNotesManage}>
      <Modal open={open} onClose={onClose} className="w-[550px]">
        <header className="flex">
          <Typography variant="title" className="flex-1">
            Dashboard Notes
          </Typography>
          <IconButton iconName="close" iconSize="lg" isCustomIcon variant="custom" onClick={onClose} />
        </header>
        <form onSubmit={handleSubmit(onSubmit)} className="mt-3">
          <div className="grid items-center gap-8">
            <TextArea {...register("dashboardNotes")} className="h-[205px]" placeholder="Dashboard notes" autoFocus maxLength={2048} />
            <Button type="submit" disabled={!isDirty || isSaving} className="w-full" variant="primary" size="lg">
              Update Notes
            </Button>
          </div>
        </form>
      </Modal>
    </RequirePermission>
  );
};
